import { z } from 'zod';
import { IdSchema } from './common.js';

/*
 * Contrato 2: formato da suíte de perguntas (contexto/<assunto>/perguntas.yaml).
 * O resultado esperado depende de quem pergunta, por causa da section access,
 * e perguntas com datas relativas usam uma data de referência fixa.
 */

const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'use AAAA-MM-DD');

/** Resultado esperado para um usuário: o número, ou a ausência de acesso ao dado. */
export const ExpectedValueSchema = z.union([z.number(), z.literal('sem_acesso')]);
export type ExpectedValue = z.infer<typeof ExpectedValueSchema>;

/** Valores de parâmetro chegam do YAML como texto ou número (2025 vira número); o núcleo trabalha com texto. */
const ParamValueSchema = z.union([z.string(), z.number()]).transform((v) => String(v));

export const ExpectedAnswerSchema = z.object({
  query: IdSchema,
  parametros: z.record(z.string(), ParamValueSchema).default({}),
  /** Chave: usuário como a fonte conhece (CLUSTER\maria). */
  resultado: z.record(z.string(), ExpectedValueSchema).default({}),
  /** Tolerância absoluta na comparação do número (padrão: 0,005, meio centavo). */
  tolerancia: z.number().min(0).optional(),
});

export const ExpectedStatusSchema = z.object({
  status: z.enum(['sem_resposta', 'esclarecimento']),
});

export const EvalQuestionSchema = z.object({
  id: z.string().min(1).optional(),
  pergunta: z.string().min(1),
  /** Esclarecimento que o usuário daria se o serviço pedir (simula a segunda volta da conversa). */
  esclarecimento: z.string().min(1).optional(),
  esperado: z.union([ExpectedAnswerSchema, ExpectedStatusSchema]),
  tags: z.array(z.string()).default([]),
});
export type EvalQuestion = z.infer<typeof EvalQuestionSchema>;

export const EvalSuiteSchema = z.object({
  /** Assunto da suíte; quando omitido, vale o da pasta onde o arquivo está. */
  assunto: IdSchema.optional(),
  data_referencia: IsoDateSchema,
  perguntas: z.array(EvalQuestionSchema).min(1),
});
export type EvalSuite = z.infer<typeof EvalSuiteSchema>;

export function isExpectedAnswer(e: EvalQuestion['esperado']): e is z.infer<typeof ExpectedAnswerSchema> {
  return 'query' in e;
}

/** Classificação de falhas da seção "Contexto e avaliação". */
export const FailureClassSchema = z.enum([
  'contexto_faltando',
  'contexto_ambiguo',
  'contexto_dificil_de_achar',
  'modelo_de_dados_ambiguo',
  'nao_classificada',
]);
export type FailureClass = z.infer<typeof FailureClassSchema>;
